/**
 * Pure row-mapper for the scholarship-offers XLSX export (Meteor parity).
 *
 * This module owns the canonical 17-column contract — order, labels, and cell
 * types — that the legacy Meteor export produced
 * (`apps/meteor/imports/api/offers/client/components/EventOffersExportBtn.vue`).
 * The Next.js export route assembles batched lookups (offers, profiles,
 * applications, offering-school names) and calls `mapOfferToExportRow` once per
 * offer. Keeping the mapping pure makes it unit-testable without a database.
 *
 * Column sourcing (Ballee):
 *   1  YEAR                  audition.opening_date → year
 *   2  VENUE                 audition.location (venue/title)
 *   3  PARTICIPANT           application.candidate_number
 *   4  NAME                  profile.first_name
 *   5  SURNAME               profile.last_name
 *   6  AGE                   computeAgeOnDate(profile.date_of_birth, opening_date)
 *   7  DATE OF BIRTH         profile.date_of_birth → MM/DD/YYYY
 *   8  GENDER                profile.gender → Meteor English label
 *   9  RESIDENCE COUNTRY     profile.country_of_residence
 *   10 PARTICIPANT EMAIL     application.fields->>'contactEmail'
 *   11 PARENT EMAIL          application.fields->>'parentContactEmail'  *
 *   12 SCHOOL EMAIL          application.fields->>'teacherContactEmail' *
 *   13 CURRENT SCHOOL        application.fields->>'currentBalletSchool' *
 *   14 OFFERING SCHOOL       resolveOfferingSchools() map (O8)
 *   15 TERM                  audition_offers.offer_type_label
 *   16 SCHOLARSHIP TIER      audition_offers.scholarship_tier
 *   17 COMMENT               audition_offers.organization_comment
 *
 *   * Columns 11/12/13 are only populated after the live-apply cutover
 *     (#1075). They render blank where the JSONB key is absent — by design,
 *     not a bug.
 */

import { computeAgeOnDate } from './age';
import { formatGenderLabel } from './format-gender-label';

/** Stable machine keys for each export column, in Meteor order. */
export type ScholarshipOfferExportColumnKey =
  | 'year'
  | 'venue'
  | 'participantReference'
  | 'participantName'
  | 'participantSurname'
  | 'age'
  | 'birthdate'
  | 'gender'
  | 'residenceCountry'
  | 'participantEmail'
  | 'parentEmail'
  | 'schoolEmail'
  | 'currentSchool'
  | 'offeringPresentingSchool'
  | 'term'
  | 'scholarship'
  | 'comment';

export interface ScholarshipOfferExportColumn {
  key: ScholarshipOfferExportColumnKey;
  /** Human-readable header label (byte-for-byte Meteor parity). */
  label: string;
  /** XLSX cell type: 'n' numeric, 's' string. */
  cellType: 'n' | 's';
}

/**
 * The 17 columns in exact Meteor order, with the same labels (including the
 * parenthetical hints on TERM and AMOUNT) and cell types.
 */
export const SCHOLARSHIP_OFFER_EXPORT_COLUMNS: readonly ScholarshipOfferExportColumn[] =
  [
    { key: 'year', label: 'YEAR', cellType: 'n' },
    { key: 'venue', label: 'VENUE', cellType: 's' },
    { key: 'participantReference', label: 'PARTICIPANT', cellType: 'n' },
    { key: 'participantName', label: 'NAME', cellType: 's' },
    { key: 'participantSurname', label: 'SURNAME', cellType: 's' },
    { key: 'age', label: 'AGE', cellType: 'n' },
    { key: 'birthdate', label: 'DATE OF BIRTH', cellType: 's' },
    { key: 'gender', label: 'GENDER', cellType: 's' },
    { key: 'residenceCountry', label: 'RESIDENCE COUNTRY', cellType: 's' },
    { key: 'participantEmail', label: 'PARTICIPANT EMAIL', cellType: 's' },
    { key: 'parentEmail', label: 'PARENT EMAIL', cellType: 's' },
    { key: 'schoolEmail', label: 'SCHOOL EMAIL', cellType: 's' },
    { key: 'currentSchool', label: 'CURRENT SCHOOL', cellType: 's' },
    {
      key: 'offeringPresentingSchool',
      label: 'OFFERING PRESENTING SCHOOL',
      cellType: 's',
    },
    {
      key: 'term',
      label: 'TERM (year/short term/summer intensive)',
      cellType: 's',
    },
    {
      key: 'scholarship',
      label: 'AMOUNT (Full Merit Scholarship/Partial Scholarship/Acceptance)',
      cellType: 's',
    },
    { key: 'comment', label: 'COMMENT', cellType: 's' },
  ] as const;

/** A fully-mapped export row: every column key → a string or number cell. */
export type ScholarshipOfferExportRow = Record<
  ScholarshipOfferExportColumnKey,
  string | number
>;

/**
 * Subset of `audition_applications.fields` the export reads. All keys optional
 * — pre-cutover applications won't carry the contact-email keys.
 */
export interface ApplicationFields {
  contactEmail?: unknown;
  parentContactEmail?: unknown;
  teacherContactEmail?: unknown;
  currentBalletSchool?: unknown;
}

export interface ScholarshipOfferRowInput {
  /** `audition.opening_date` (ISO date string) — drives YEAR + AGE reference. */
  openingDate: string | null;
  /** `audition.location` (or title) — VENUE column. */
  venue: string | null;
  /** `audition_applications.candidate_number` — fallback for PARTICIPANT. */
  candidateNumber: number | null;
  /** `audition_applications.fields` JSONB (already parsed). */
  applicationFields: ApplicationFields | null;
  /** `profiles.first_name`. */
  firstName: string | null;
  /** `profiles.last_name`. */
  lastName: string | null;
  /** `profiles.date_of_birth` (ISO date string). */
  dateOfBirth: string | null;
  /** `profiles.gender` enum value. */
  gender: string | null;
  /** `profiles.country_of_residence`. */
  residenceCountry: string | null;
  /** Resolved offering-school name (O8). Empty string when unresolved. */
  offeringSchool: string;
  /** `audition_offers.offer_type_label` — TERM. */
  offerTypeLabel: string | null;
  /** `audition_offers.scholarship_tier` — AMOUNT / scholarship tier. */
  scholarshipTier: string | null;
  /** `audition_offers.organization_comment` — COMMENT. */
  organizationComment: string | null;
}

/** Reads a JSON value as a trimmed string, or '' when absent/non-string. */
function jsonString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Formats an ISO date (`YYYY-MM-DD`) as `MM/DD/YYYY` to match the Meteor
 * export, which reformatted `DD/MM/YYYY` source dates to US order. Returns ''
 * for absent/invalid input. Timezone-safe: parses the date parts directly
 * rather than constructing a `Date` (avoids the UTC-shift class).
 */
export function formatBirthdate(isoDate: string | null): string {
  if (!isoDate) return '';

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!match) return '';

  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
}

/**
 * Maps a single offer + its joined rows to the 17-column export row.
 *
 * Pure function: no I/O, no client. The route does all batched fetching and
 * passes the per-offer slice here.
 */
export function mapOfferToExportRow(
  input: ScholarshipOfferRowInput,
): ScholarshipOfferExportRow {
  const year = input.openingDate
    ? Number.parseInt(input.openingDate.slice(0, 4), 10)
    : '';

  const fields = input.applicationFields ?? {};

  const age = computeAgeOnDate(input.dateOfBirth, input.openingDate);

  // PARTICIPANT: the organizer-assigned candidate number.
  const participantReference = input.candidateNumber ?? '';

  return {
    year,
    venue: input.venue ?? '',
    participantReference,
    participantName: input.firstName ?? '',
    participantSurname: input.lastName ?? '',
    age: age ?? '',
    birthdate: formatBirthdate(input.dateOfBirth),
    gender: formatGenderLabel(input.gender),
    residenceCountry: input.residenceCountry ?? '',
    participantEmail: jsonString(fields.contactEmail),
    parentEmail: jsonString(fields.parentContactEmail),
    schoolEmail: jsonString(fields.teacherContactEmail),
    currentSchool: jsonString(fields.currentBalletSchool),
    offeringPresentingSchool: input.offeringSchool,
    term: input.offerTypeLabel ?? '',
    scholarship: input.scholarshipTier ?? '',
    comment: input.organizationComment ?? '',
  };
}

/**
 * Sort comparator for export rows by participant reference (ascending),
 * matching Meteor's `_.sortBy(offers, ['invitation.reference'])`.
 * Numeric references sort numerically; string references sort
 * lexicographically; numbers sort before strings.
 */
export function compareByParticipantReference(
  a: ScholarshipOfferExportRow,
  b: ScholarshipOfferExportRow,
): number {
  const av = a.participantReference;
  const bv = b.participantReference;

  const aNum = typeof av === 'number';
  const bNum = typeof bv === 'number';

  if (aNum && bNum) return (av as number) - (bv as number);
  if (aNum) return -1;
  if (bNum) return 1;
  return String(av).localeCompare(String(bv));
}
